import { useState, useEffect, useRef, useCallback, memo, useMemo } from 'react';
import {
  Mic,
  Send,
  User,
  Bot,
  MessageSquare,
  Volume2,
  RefreshCw,
  Loader2,
  Sparkles,
  Square,
  Globe,
  Zap,
  Activity,
} from 'lucide-react';
import { ContentItem } from '@/types/ai-learn.types';
import { learningAssistantV2Service } from '@/services/learning-assistant-v2.service';
import type {
  TextChunk,
  TextChatComplete,
  ChatMetrics,
  ChatEvent,
  ChatError,
  ConnectionSuccess,
} from '@/services/learning-assistant-v2.service';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Select, { components, StylesConfig } from 'react-select';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  suggestions?: string[];
  suggestionsLoading?: boolean; // OPTIMIZATION: Track if suggestions are being generated
}

interface CourseModule {
  _id?: string;
  id?: string;
  title: string;
}

interface CourseInfo {
  title: string;
}

interface AILiveChatV2Props {
  course: CourseInfo;
  courseId: string;
  currentModule: CourseModule | null;
  currentContent: ContentItem | null;
}

// Language options (same as V1 AILiveChat)
import { languageOptionsDetailed } from '@/constants/languageOptions';

const LANGUAGE_OPTIONS = languageOptionsDetailed;

// Transform language options for react-select
interface LanguageOption {
  value: string;
  label: string;
  flag: string;
  name: string;
}

const languageSelectOptions: LanguageOption[] = LANGUAGE_OPTIONS.map((lang) => ({
  value: lang.code,
  label: lang.name,
  flag: lang.flag,
  name: lang.name,
}));

// Custom components for react-select
const CustomOption = (props: any) => {
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <span className="text-base">{props.data.flag}</span>
        <span className="text-sm">{props.data.label}</span>
      </div>
    </components.Option>
  );
};

const CustomSingleValue = (props: any) => {
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-1.5">
        <span className="text-sm">{props.data.flag}</span>
        <span className="text-[11px] font-medium truncate">{props.data.label.split(' ')[0]}</span>
      </div>
    </components.SingleValue>
  );
};

// Custom styles for react-select
const customSelectStyles: StylesConfig<LanguageOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: '32px',
    height: '32px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(8px)',
    borderColor: state.isFocused ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
    boxShadow: state.isFocused ? '0 0 0 1px rgba(255, 255, 255, 0.3)' : 'none',
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: '32px',
    padding: '0 8px',
  }),
  input: (base) => ({
    ...base,
    margin: '0',
    padding: '0',
    color: 'white',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '4px',
    color: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      color: 'white',
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
    overflow: 'hidden',
    zIndex: 100,
  }),
  menuList: (base) => ({
    ...base,
    padding: '4px',
    maxHeight: '240px',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#eef2ff'
      : state.isFocused
      ? '#f5f3ff'
      : 'white',
    color: state.isSelected ? '#4f46e5' : '#374151',
    cursor: 'pointer',
    padding: '8px 12px',
    borderRadius: '6px',
    fontWeight: state.isSelected ? '600' : '400',
    '&:active': {
      backgroundColor: '#e0e7ff',
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: 'white',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.6)',
  }),
};

// Helper to create messages
const createUserMessage = (content: string): Message => ({
  id: `msg_${Date.now()}`,
  role: 'user',
  content,
  timestamp: new Date(),
});

const createAssistantMessage = (content: string, suggestions?: string[]): Message => ({
  id: `msg_${Date.now() + 1}`,
  role: 'assistant',
  content,
  timestamp: new Date(),
  suggestions,
});

const createStreamingMessage = (): Message => ({
  id: `msg_${Date.now() + 1}`,
  role: 'assistant',
  content: '',
  timestamp: new Date(),
  isStreaming: true,
});

// Suggestion translations map
const SUGGESTIONS_I18N: Record<string, Record<string, string[]>> = {
  video: {
    'en-US': ["Summarize the key points from this video", "What are the main concepts covered?", "Give me a quiz on this video content"],
    'en-GB': ["Summarise the key points from this video", "What are the main concepts covered?", "Give me a quiz on this video content"],
    'hi-IN': ["इस वीडियो के मुख्य बिंदुओं को संक्षेप में बताएं", "मुख्य अवधारणाएं क्या हैं?", "इस वीडियो पर एक क्विज़ दें"],
    'es-ES': ["Resume los puntos clave de este video", "¿Cuáles son los conceptos principales?", "Dame un cuestionario sobre este contenido"],
    'fr-FR': ["Résumez les points clés de cette vidéo", "Quels sont les concepts principaux?", "Donnez-moi un quiz sur ce contenu"],
    'de-DE': ["Fassen Sie die wichtigsten Punkte dieses Videos zusammen", "Was sind die Hauptkonzepte?", "Geben Sie mir ein Quiz zu diesem Inhalt"],
  },
  text: {
    'en-US': ["Explain the difficult concepts here", "Give me real-world examples", "Test my understanding with questions"],
    'hi-IN': ["यहां कठिन अवधारणाओं को समझाएं", "मुझे वास्तविक दुनिया के उदाहरण दें", "प्रश्नों से मेरी समझ का परीक्षण करें"],
    'es-ES': ["Explica los conceptos difíciles aquí", "Dame ejemplos del mundo real", "Pon a prueba mi comprensión con preguntas"],
    'fr-FR': ["Expliquez les concepts difficiles ici", "Donnez-moi des exemples concrets", "Testez ma compréhension avec des questions"],
    'de-DE': ["Erklären Sie die schwierigen Konzepte hier", "Geben Sie mir Beispiele aus der Praxis", "Testen Sie mein Verständnis mit Fragen"],
  },
  default: {
    'en-US': ["Explain this content in simple terms", "What are the key learning objectives?", "How does this relate to real-world applications?"],
    'hi-IN': ["इस सामग्री को सरल शब्दों में समझाएं", "मुख्य शिक्षण उद्देश्य क्या हैं?", "यह वास्तविक दुनिया के अनुप्रयोगों से कैसे संबंधित है?"],
    'es-ES': ["Explica este contenido en términos simples", "¿Cuáles son los objetivos de aprendizaje clave?", "¿Cómo se relaciona esto con aplicaciones del mundo real?"],
    'fr-FR': ["Expliquez ce contenu en termes simples", "Quels sont les objectifs d'apprentissage clés?", "Comment cela se rapporte-t-il aux applications du monde réel?"],
    'de-DE': ["Erklären Sie diesen Inhalt in einfachen Worten", "Was sind die wichtigsten Lernziele?", "Wie bezieht sich dies auf reale Anwendungen?"],
  },
  module: {
    'en-US': ["Show me the learning path for this module", "What are the prerequisites?"],
    'hi-IN': ["इस मॉड्यूल के लिए सीखने का मार्ग दिखाएं", "पूर्वापेक्षाएँ क्या हैं?"],
    'es-ES': ["Muéstrame la ruta de aprendizaje para este módulo", "¿Cuáles son los requisitos previos?"],
    'fr-FR': ["Montrez-moi le parcours d'apprentissage pour ce module", "Quels sont les prérequis?"],
    'de-DE': ["Zeigen Sie mir den Lernpfad für dieses Modul", "Was sind die Voraussetzungen?"],
  },
  course: {
    'en-US': ["What are the main topics covered?", "How should I approach this course?"],
    'hi-IN': ["मुख्य विषय क्या हैं?", "मुझे इस पाठ्यक्रम को कैसे करना चाहिए?"],
    'es-ES': ["¿Cuáles son los temas principales cubiertos?", "¿Cómo debo abordar este curso?"],
    'fr-FR': ["Quels sont les principaux sujets couverts?", "Comment devrais-je aborder ce cours?"],
    'de-DE': ["Was sind die Hauptthemen?", "Wie sollte ich diesen Kurs angehen?"],
  }
};

// Generate contextual suggestions based on language
const generateContextualSuggestions = (
  currentContent: ContentItem | null,
  currentModule: CourseModule | null,
  course: CourseInfo,
  language: string
): string[] => {
  // Default to English if language not in map
  const lang = SUGGESTIONS_I18N.video[language] ? language : 'en-US';

  if (currentContent) {
    switch (currentContent.type) {
      case 'video_presentation':
      case 'avatar_video':
        return SUGGESTIONS_I18N.video[lang] || SUGGESTIONS_I18N.video['en-US'];
      case 'html_with_audio':
      case 'html':
        return SUGGESTIONS_I18N.text[lang] || SUGGESTIONS_I18N.text['en-US'];
      default:
        return SUGGESTIONS_I18N.default[lang] || SUGGESTIONS_I18N.default['en-US'];
    }
  } else if (currentModule) {
    const moduleSuggestions = SUGGESTIONS_I18N.module[lang] || SUGGESTIONS_I18N.module['en-US'];
    // Add dynamic suggestion with module title
    const moduleTitle = currentModule.title;
    const dynamicSuggestion = lang === 'hi-IN'
      ? `मैं ${moduleTitle} में क्या सीखूंगा?`
      : lang === 'es-ES'
      ? `¿Qué aprenderé en ${moduleTitle}?`
      : lang === 'fr-FR'
      ? `Que vais-je apprendre dans ${moduleTitle}?`
      : lang === 'de-DE'
      ? `Was werde ich in ${moduleTitle} lernen?`
      : `What will I learn in ${moduleTitle}?`;
    return [dynamicSuggestion, ...moduleSuggestions];
  }

  const courseSuggestions = SUGGESTIONS_I18N.course[lang] || SUGGESTIONS_I18N.course['en-US'];
  // Add dynamic suggestion with course title
  const courseTitle = course?.title;
  const dynamicSuggestion = lang === 'hi-IN'
    ? `${courseTitle} पाठ्यक्रम के बारे में बताएं`
    : lang === 'es-ES'
    ? `Cuéntame sobre el curso ${courseTitle}`
    : lang === 'fr-FR'
    ? `Parlez-moi du cours ${courseTitle}`
    : lang === 'de-DE'
    ? `Erzählen Sie mir über den Kurs ${courseTitle}`
    : `Tell me about the ${courseTitle} course`;
  return [dynamicSuggestion, ...courseSuggestions];
};

// Memoized Message Component
interface MessageComponentProps {
  message: Message;
  onSendSuggestion: (suggestion: string) => void;
  isLoading: boolean;
}

const MessageComponent = memo(({
  message,
  onSendSuggestion,
  isLoading
}: MessageComponentProps) => {
  const isUser = message.role === 'user';

  // OPTIMIZATION Phase 2: Debounce markdown rendering during streaming
  // Only re-render every 100ms during streaming to reduce CPU usage by 30-50%
  const [debouncedContent, setDebouncedContent] = useState(message.content);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (message.isStreaming) {
      // During streaming: debounce updates to reduce re-renders
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setDebouncedContent(message.content);
      }, 100); // 100ms debounce - imperceptible to users

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    } else {
      // When complete: update immediately
      setDebouncedContent(message.content);
    }
  }, [message.content, message.isStreaming]);

  // OPTIMIZATION Phase 2: Memoize expensive markdown components
  const markdownComponents = useMemo(() => ({
    p: ({ children }: any) => <p className="mb-2 last:mb-0">{children}</p>,
    ul: ({ children }: any) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
    ol: ({ children }: any) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
    li: ({ children }: any) => <li className="ml-2">{children}</li>,
    code: ({ node, inline, className, children, ...props }: any) => {
      return inline ? (
        <code className="bg-gray-100 text-indigo-700 px-1 py-0.5 rounded text-[10px] font-mono" {...props}>{children}</code>
      ) : (
        <code className="block bg-gray-900 text-gray-100 p-2 rounded text-[10px] font-mono overflow-x-auto" {...props}>{children}</code>
      );
    },
    strong: ({ children }: any) => <strong className="font-bold text-indigo-700">{children}</strong>,
    em: ({ children }: any) => <em className="italic">{children}</em>,
    a: ({ href, children }: any) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline">
        {children}
      </a>
    ),
    h1: ({ children }: any) => <h1 className="text-sm font-bold mb-2 mt-3">{children}</h1>,
    h2: ({ children }: any) => <h2 className="text-xs font-bold mb-2 mt-2">{children}</h2>,
    h3: ({ children }: any) => <h3 className="text-xs font-semibold mb-1 mt-2">{children}</h3>,
  }), []);

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-2 animate-fadeIn`}>
      <div
        className={`max-w-[90%] sm:max-w-[80%] rounded-xl shadow-sm ${
          isUser
            ? 'bg-gradient-to-br from-indigo-600 to-indigo-700 text-white'
            : 'bg-white text-gray-800 border border-gray-100'
        }`}
      >
        <div className="flex items-start gap-2 p-2.5">
          {!isUser && (
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Bot className="w-3 h-3 text-white" />
            </div>
          )}
          {isUser && (
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
              <User className="w-3 h-3" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            {/* Message content */}
            <div className="text-xs leading-relaxed break-words">
              {isUser ? (
                // User messages: plain text
                <div className="whitespace-pre-wrap">{message.content}</div>
              ) : (
                // Assistant messages: render markdown with debounced content
                <div className="prose prose-xs prose-slate max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {debouncedContent}
                  </ReactMarkdown>
                </div>
              )}
              {message.isStreaming && (
                <span className="inline-block w-1.5 h-3 ml-1 bg-indigo-600 animate-pulse" />
              )}
            </div>

            {/* Suggestions */}
            {message.suggestions && message.suggestions.length > 0 && !message.isStreaming && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {message.suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => onSendSuggestion(suggestion)}
                    disabled={isLoading}
                    className="group px-2 py-1 text-[10px] bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg hover:from-indigo-100 hover:to-purple-100 hover:border-indigo-300 transition-all duration-200 text-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Sparkles className="w-2.5 h-2.5" />
                    <span>{suggestion}</span>
                  </button>
                ))}
              </div>
            )}

            {/* OPTIMIZATION: Suggestions Loading Indicator */}
            {message.suggestionsLoading && !message.isStreaming && (
              <div className="mt-2 flex items-center gap-1.5 text-[10px] text-indigo-600">
                <Sparkles className="w-3 h-3 animate-pulse" />
                <span className="animate-pulse">Generating suggestions</span>
                <span className="inline-flex gap-0.5">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </div>
            )}

            {/* Timestamp */}
            <div className={`text-[10px] mt-1.5 ${
              isUser ? 'text-white/60' : 'text-gray-400'
            }`}>
              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

MessageComponent.displayName = 'MessageComponent';

export default function AILiveChatV2({
  course,
  courseId,
  currentModule,
  currentContent
}: AILiveChatV2Props) {
  // State
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected');
  const [firstChunkTime, setFirstChunkTime] = useState<number | null>(null);
  const [currentEventStatus, setCurrentEventStatus] = useState<string>('');
  const [showLoader, setShowLoader] = useState(false); // Separate loader state with minimum display time

  // Audio state
  const [audioMode, setAudioMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [transcriptionText, setTranscriptionText] = useState('');

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingMessageIdRef = useRef<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastTranscriptionTimeRef = useRef<number>(Date.now());
  const lastInterimTranscriptionRef = useRef<string>(''); // BUG FIX: Track last interim for fallback
  const finalTranscriptionReceivedRef = useRef<boolean>(false); // BUG FIX: Track if final received
  // OPTIMIZATION Phase 3.1: Web Worker for audio processing
  const audioWorkerRef = useRef<Worker | null>(null);
  const audioChunkIdRef = useRef<number>(0);
  // LOCAL SILENCE DETECTION: Backup mechanism that doesn't rely on server transcriptions
  const lastAudioActivityRef = useRef<number>(Date.now());
  const localSilenceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // OPTIMIZATION Phase 3.1: Initialize Web Worker for audio processing
  useEffect(() => {
    // Create worker on component mount
    try {
      audioWorkerRef.current = new Worker('/audio-worker.js');
      console.log('[AILiveChatV2] Audio worker initialized');

      // Handle messages from worker
      audioWorkerRef.current.onmessage = (e) => {
        const { success, base64, error, chunkId } = e.data;

        if (success) {
          // Send processed audio chunk to server
          try {
            learningAssistantV2Service.sendAudioChunk(base64);
          } catch (sendError) {
            console.error('[AILiveChatV2] Error sending audio chunk:', sendError);
          }
        } else {
          console.error('[AILiveChatV2] Worker processing error:', error);
        }
      };

      // Handle worker errors
      audioWorkerRef.current.onerror = (error) => {
        console.error('[AILiveChatV2] Audio worker error:', error);
        // Fallback: will use main thread processing
      };

    } catch (error) {
      console.error('[AILiveChatV2] Failed to create audio worker, will use main thread fallback:', error);
    }

    // Cleanup on unmount
    return () => {
      if (audioWorkerRef.current) {
        audioWorkerRef.current.terminate();
        audioWorkerRef.current = null;
        console.log('[AILiveChatV2] Audio worker terminated');
      }
    };
  }, []);

  // Auto-scroll
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Audio handler functions (defined before useEffect)
  // Handle audio transcription chunks
  const handleAudioTranscription = useCallback((chunk: any) => {
    const currentTime = Date.now();
    const timeSinceLastTranscription = lastTranscriptionTimeRef.current
      ? currentTime - lastTranscriptionTimeRef.current
      : 0;

    console.log('[AI VOICE CHECK LOG] [AILiveChatV2] 🔊 VOICE DETECTED - Transcription received:', {
      text: chunk.text,
      is_final: chunk.is_final,
      confidence: chunk.confidence,
      time_since_last_voice: timeSinceLastTranscription + 'ms'
    });

    // Update last transcription time for silence detection
    lastTranscriptionTimeRef.current = currentTime;

    // Reset silence timer (clear previous timer)
    if (silenceTimerRef.current) {
      console.log('[AI VOICE CHECK LOG] [AILiveChatV2] 🔄 Voice detected - Resetting silence timer');
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    // IMPROVED: Set new silence timer - auto-stop after 4 seconds of silence
    // Only set timer for interim results (real-time speech detection)
    if (!chunk.is_final) {
      const silenceTimeout = 4000; // 4 seconds
      const timerStartTime = Date.now();

      console.log('[AI VOICE CHECK LOG] [AILiveChatV2] ⏰ Starting silence timer - will auto-stop in', silenceTimeout / 1000, 'seconds');

      silenceTimerRef.current = setTimeout(() => {
        const elapsedTime = Date.now() - timerStartTime;
        console.log('[AI VOICE CHECK LOG] [AILiveChatV2] 🔕 SILENCE DETECTED - No voice for', elapsedTime / 1000, 'seconds');
        console.log('[AI VOICE CHECK LOG] [AILiveChatV2] ⏱️ Auto-stopping recording due to silence...');

        // Clear timer
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }

        // Stop recording if still active
        if (mediaRecorderRef.current && typeof (mediaRecorderRef.current as any).stop === 'function') {
          try {
            (mediaRecorderRef.current as any).stop();
            console.log('[AI VOICE CHECK LOG] [AILiveChatV2] ✅ Recording stopped successfully (silence detected)');
            // toast.success('Recording stopped (silence detected)', {
            //   duration: 2000,
            //   icon: '⏱️'
            // });
            console.log('[AILiveChatV2] Recording stopped (silence detected)');
          } catch (error) {
            console.error('[AI VOICE CHECK LOG] [AILiveChatV2] ❌ Error auto-stopping recording:', error);
          }
        } else {
          console.log('[AI VOICE CHECK LOG] [AILiveChatV2] ⚠️ MediaRecorder not available or already stopped');
        }
      }, silenceTimeout);
    } else {
      console.log('[AI VOICE CHECK LOG] [AILiveChatV2] ✅ Final transcription - No silence timer needed');
    }

    // BUG FIX: Track interim transcriptions for fallback
    if (!chunk.is_final && chunk.text && chunk.text.trim().length > 0) {
      lastInterimTranscriptionRef.current = chunk.text;
    }

    // BUG FIX 3: Update transcription text without repeating
    setTranscriptionText(prev => {
      if (chunk.is_final) {
        // Final result - this is complete transcription segment
        console.log('[AILiveChatV2] ✅ Final transcription:', chunk.text);
        finalTranscriptionReceivedRef.current = true; // BUG FIX: Mark final received

        // Clear transcription on final (will be shown in message)
        // Don't accumulate final text here to avoid duplication
        return '';
      } else {
        // Interim result - Google Cloud sends complete transcription so far
        console.log('[AILiveChatV2] 📝 Interim transcription:', chunk.text);

        // Simply show the interim text with marker (no accumulation)
        // This is the real-time transcription as user speaks
        return chunk.text + '|INTERIM|';
      }
    });

    // Add final transcription as user message
    if (chunk.is_final) {
      const userMessage = createUserMessage(chunk.text);
      setMessages(prev => [...prev, userMessage]);
    }
  }, []); // No dependencies - uses refs for timer management

  // Handle audio chunks from server
  const handleAudioChunk = useCallback((chunk: any) => {
    console.log('[AILiveChatV2] Audio chunk received:', chunk.chunk_id);

    // Convert base64 to blob
    const binary = atob(chunk.audio_data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([array], { type: `audio/${chunk.format}` });

    audioChunksRef.current.push(blob);

    // Start playback when first chunk arrives
    if (audioChunksRef.current.length === 1 && audioPlayerRef.current) {
      const url = URL.createObjectURL(blob);
      audioPlayerRef.current.src = url;
      audioPlayerRef.current.play().then(() => {
        setIsPlayingAudio(true);
      }).catch(err => {
        console.error('Audio playback failed:', err);
        toast.error('Audio playback failed');
      });
    }
  }, []);

  // Handle audio complete
  const handleAudioComplete = useCallback((data: any) => {
    console.log('[AILiveChatV2] Audio complete:', data);

    // BUG FIX: Check if we received a final transcription
    // If not, use last interim as fallback to create user message
    const timeoutId = setTimeout(() => {
      if (!finalTranscriptionReceivedRef.current && lastInterimTranscriptionRef.current.trim().length > 0) {
        console.warn('[AILiveChatV2] ⚠️ No final transcription received, using last interim as fallback');
        console.log('[AILiveChatV2] Fallback transcript:', lastInterimTranscriptionRef.current);

        // Create user message from last interim transcription
        const userMessage = createUserMessage(lastInterimTranscriptionRef.current);
        setMessages(prev => {
          // Check if last message is already from user with this text (avoid duplicate)
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.role === 'user' && lastMsg.content === lastInterimTranscriptionRef.current) {
            return prev; // Already added
          }
          return [...prev, userMessage];
        });

        toast.info('Used interim transcription (no final result received)', { duration: 2000 });
      }

      // Reset refs for next recording
      finalTranscriptionReceivedRef.current = false;
      lastInterimTranscriptionRef.current = '';
    }, 500); // Wait 500ms for final transcription to arrive

    setIsLoading(false);
    setIsRecording(false);
    setTranscriptionText('');

    // Finalize any streaming message (remove the blue cursor)
    // This is critical for voice mode where the text response might still be streaming
    setMessages(prev => {
      // Check if there's any message with isStreaming: true
      let hasStreamingMessage = false;
      const updatedMessages = prev.map((msg, index) => {
        if (msg.isStreaming && msg.role === 'assistant') {
          hasStreamingMessage = true;
          console.log('[AILiveChatV2] Finalizing streaming message in audio complete');
          return {
            ...msg,
            isStreaming: false,
          };
        }
        return msg;
      });

      if (hasStreamingMessage) {
        return updatedMessages;
      }
      return prev;
    });

    // Clear streaming ref
    streamingMessageIdRef.current = null;

    // Clear audio buffer after a delay
    setTimeout(() => {
      audioChunksRef.current = [];
    }, 1000);
  }, []);

  // Connect to WebSocket
  const connectToAI = useCallback(async () => {
    if (isConnected || isConnecting) return;

    console.log('[AILiveChatV2] Starting connection, setting connecting state...');

    // Set connecting state FIRST
    setIsConnecting(true);
    setConnectionStatus('connecting');
    setShowLoader(true); // Show loader immediately

    // Track minimum display time
    const loaderStartTime = Date.now();
    const minimumLoaderTime = 1500; // 1.5 seconds minimum

    // Add a small delay to ensure UI updates before async operation
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      await learningAssistantV2Service.connect({
        onConnect: async (data: ConnectionSuccess) => {
          console.log('[AILiveChatV2] Connected:', data);

          // Calculate remaining time to show loader
          const elapsedTime = Date.now() - loaderStartTime;
          const remainingTime = Math.max(0, minimumLoaderTime - elapsedTime);

          console.log('[AILiveChatV2] Connection completed in', elapsedTime, 'ms, waiting', remainingTime, 'ms more for loader');

          // Wait for minimum loader display time
          if (remainingTime > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingTime));
          }

          console.log('[AILiveChatV2] Setting connected state, clearing connecting...');
          setIsConnected(true);
          setIsConnecting(false);
          setConnectionStatus('connected');
          setShowLoader(false); // Hide loader after minimum time

          // Only show toast and welcome message for NEW connections
          // If reusing existing connection, don't reset messages
          if (data.message === 'Reusing existing connection') {
            console.log('[AILiveChatV2] Reusing connection, messages will be restored from persistence');

            // IMPORTANT: Clear streaming ref so new chunks create fresh messages
            streamingMessageIdRef.current = null;

            // BUG FIX: Show reconnection success notification
            toast.success('Reconnected to AI Assistant', {
              duration: 3000,
              description: 'Your session has been restored.'
            });

            // Don't add any messages - they're restored from persistence
            // No welcome message needed when reconnecting
          } else {
            // BUG FIX: Show connection success notification
            toast.success('AI Assistant connected', {
              duration: 3000,
              description: 'Ready to help with your learning!'
            });
            console.log('[AILiveChatV2] AI Assistant connected');

            // Add welcome message only for new connections
            const welcomeMessage = createAssistantMessage(
              `Hello! I'm your AI learning assistant. I can help you with "${course?.title}". ${currentModule ? `You're in module: "${currentModule.title}".` : ''} ${currentContent ? `Looking at: "${currentContent.title}".` : ''} How can I assist you?`,
              generateContextualSuggestions(currentContent, currentModule, course, selectedLanguage)
            );
            setMessages([welcomeMessage]);
            streamingMessageIdRef.current = null;
          }
        },

        onChunk: (chunk: TextChunk) => {
          console.log('[AILiveChatV2] Chunk received:', {
            chunk_id: chunk.chunk_id,
            text_length: chunk.text?.length,
            current_streaming_id: streamingMessageIdRef.current
          });

          setSessionId(chunk.session_id);

          // Sync session ID with service
          learningAssistantV2Service.setSessionId(chunk.session_id);

          // Update or create streaming message
          setMessages(prev => {
            console.log('[AILiveChatV2] Updating messages, current count:', prev.length);
            const lastMessage = prev[prev.length - 1];

            if (lastMessage && lastMessage.isStreaming && lastMessage.id === streamingMessageIdRef.current) {
              // Append to existing streaming message
              console.log('[AILiveChatV2] Appending to existing streaming message');
              return [
                ...prev.slice(0, -1),
                {
                  ...lastMessage,
                  content: lastMessage.content + chunk.text
                }
              ];
            } else {
              // Create new streaming message
              console.log('[AILiveChatV2] Creating new streaming message');
              const newMessage = createStreamingMessage();
              streamingMessageIdRef.current = newMessage.id;
              return [...prev, { ...newMessage, content: chunk.text }];
            }
          });
        },

        onComplete: (data: TextChatComplete) => {
          console.log('✅ [UI] Received complete event (OPTIMIZATION: suggestions will arrive async)');

          setIsLoading(false);
          setCurrentEventStatus('');
          setFirstChunkTime(null);

          // OPTIMIZATION: Finalize streaming message immediately WITHOUT suggestions
          // Suggestions will be added via onSuggestionsReady when they arrive
          setMessages(prev => {
            const lastMessage = prev[prev.length - 1];
            if (lastMessage && lastMessage.isStreaming) {
              return [
                ...prev.slice(0, -1),
                {
                  ...lastMessage,
                  isStreaming: false,
                  suggestionsLoading: true, // OPTIMIZATION: Show loading indicator
                  // No suggestions initially - will be added by onSuggestionsReady
                }
              ];
            }
            return prev;
          });

          streamingMessageIdRef.current = null;
        },

        // OPTIMIZATION: New handler for async suggestions (Phase 1)
        onSuggestionsReady: (suggestions: string[]) => {
          console.log('✨ [UI] Async suggestions received:', {
            suggestions_count: suggestions.length,
            suggestions: suggestions,
            selected_language: selectedLanguage
          });

          // Update the last assistant message with suggestions and remove loading state
          setMessages(prev => {
            const updated = [...prev];
            // Find the last assistant message (most recent)
            for (let i = updated.length - 1; i >= 0; i--) {
              if (updated[i].role === 'assistant' && !updated[i].isStreaming) {
                // Only add suggestions if not already present
                if (!updated[i].suggestions || updated[i].suggestions?.length === 0) {
                  updated[i] = {
                    ...updated[i],
                    suggestions: suggestions.length > 0
                      ? suggestions
                      : generateContextualSuggestions(currentContent, currentModule, course, selectedLanguage),
                    suggestionsLoading: false // OPTIMIZATION: Hide loading indicator
                  };
                }
                break;
              }
            }
            return updated;
          });
        },

        onMetrics: (metrics: ChatMetrics) => {
          console.log('[AILiveChatV2] First chunk:', metrics.first_chunk_time_ms, 'ms');
          setFirstChunkTime(metrics.first_chunk_time_ms);
        },

        onEvent: (event: ChatEvent) => {
          console.log('[AILiveChatV2] Event:', event.type, event);

          // Update status display
          let statusMessage = '';

          switch (event.type) {
            case 'session_init':
              statusMessage = 'Initializing session...';
              break;
            case 'context_loading':
              statusMessage = 'Loading learning context...';
              break;
            case 'context_loaded':
              statusMessage = 'Context loaded ✓';
              break;
            case 'generating_response':
              statusMessage = 'Generating response...';
              break;
            case 'audio_processing':
              // @ts-ignore - event.data might have operation
              const operation = event.data?.operation;
              if (operation === 'stt') {
                statusMessage = 'Transcribing audio...';
              } else if (operation === 'tts') {
                statusMessage = 'Converting to speech...';
              } else if (operation === 'streaming_transcription') {
                statusMessage = 'Live transcription active...';
              } else if (operation === 'collecting_audio') {
                statusMessage = 'Collecting audio...';
              } else {
                statusMessage = 'Processing audio...';
              }
              break;
            case 'audio_generation_progress':
              // @ts-ignore - event.data might have chunks_generated
              const chunks = event.data?.chunks_generated;
              statusMessage = `Generating audio... (${chunks || 0} chunks)`;
              break;
            case 'transcription_fallback_used':
              // BUG FIX: Handle fallback transcription notification
              console.warn('[AILiveChatV2] ⚠️ Backend used interim transcription as fallback');
              // @ts-ignore - event.data might have message and transcript
              const fallbackMsg = event.data?.message;
              const fallbackTranscript = event.data?.transcript;
              if (fallbackTranscript) {
                console.log('[AILiveChatV2] Fallback transcript from backend:', fallbackTranscript);
                // Backend already generated response from this, just log it
                toast.info('Using interim transcription', { duration: 2000 });
              }
              statusMessage = '';
              break;
            case 'complete':
              statusMessage = '';
              break;
            case 'error': {
              // Streaming error surfaced as an event (e.g. out-of-credits). Show the same message
              // text as the rest of the app (the backend error_description).
              setIsLoading(false);
              setIsRecording(false);
              toast.error(
                event.error || 'Failed to process your message. Please try again.',
                { id: 'chat-error', duration: 6000 }, // shared id → no duplicate with the chat:error handler
              );
              statusMessage = '';
              break;
            }
            default:
              statusMessage = '';
          }

          setCurrentEventStatus(statusMessage);
        },

        onError: (error: ChatError) => {
          console.error('[AILiveChatV2] Error:', error);
          setIsLoading(false);
          setCurrentEventStatus('');
          setIsRecording(false); // Stop recording on any error

          // Handle different error codes with appropriate UI feedback
          switch (error.code) {
            case 'MAX_RECONNECT_ATTEMPTS':
            case 'RECONNECT_FAILED':
              // Connection failed after all retry attempts
              setIsConnected(false);
              setConnectionStatus('disconnected');
              setIsConnecting(false);
              toast.error('Unable to reconnect to AI Assistant', {
                duration: 10000,
                description: 'Please check if the server is running and try again.',
                action: {
                  label: 'Try Again',
                  onClick: () => {
                    console.log('[AILiveChatV2] User clicked try again after reconnection failed');
                    connectToAI();
                  }
                }
              });
              break;

            case 'RESPONSE_TIMEOUT':
              // AI response took too long
              toast.error('Response timeout', {
                duration: 5000,
                description: 'The AI took too long to respond. Please try again.',
                action: {
                  label: 'Retry',
                  onClick: () => {
                    console.log('[AILiveChatV2] User retrying after timeout');
                    // User can send message again
                  }
                }
              });
              break;

            case 'AUTH_EXPIRED':
              // Token expired and refresh failed
              setIsConnected(false);
              setConnectionStatus('disconnected');
              toast.error('Session Expired', {
                duration: 8000,
                description: 'Your session has expired. Please log in again.',
                action: {
                  label: 'Login',
                  onClick: () => {
                    window.location.href = '/login';
                  }
                }
              });
              break;

            case 'TEXT_CHAT_ERROR':
            case 'AUDIO_CHAT_ERROR': {
              // Show the same message text as the rest of the app (the backend error_description).
              toast.error(
                error.error_description ||
                error.error ||
                'Failed to process your message. Please try again.',
                { id: 'chat-error', duration: 6000 }, // shared id → no duplicate with the chat:event 'error' handler
              );
              break;
            }

            case 'CONTEXT_UPDATE_ERROR':
              // Failed to update learning context
              toast.error('Context Update Failed', {
                duration: 4000,
                description: error.error || 'Failed to update learning context.'
              });
              break;

            case 'SESSION_END_ERROR':
              // Failed to end session cleanly
              console.warn('[AILiveChatV2] Session end error (non-critical):', error.error);
              // Don't show toast for this - it's not critical
              break;

            default:
              // Generic error fallback
              toast.error(error.error || 'An error occurred', {
                duration: 4000,
                description: error.code ? `Error code: ${error.code}` : 'Please try again or reconnect.'
              });
              break;
          }
        },

        onDisconnect: () => {
          console.log('[AILiveChatV2] Disconnected');
          setIsConnected(false);
          setConnectionStatus('reconnecting'); // BUG FIX: Set to reconnecting instead of disconnected
          setIsConnecting(false);
          setIsLoading(false);
          setIsRecording(false);

          // BUG FIX: Show notification with info that auto-reconnection will happen
          toast.error('Disconnected from AI Assistant', {
            duration: 8000, // Longer duration to cover reconnection time
            description: 'Attempting to reconnect automatically...',
            action: {
              label: 'Reconnect Now',
              onClick: () => {
                console.log('[AILiveChatV2] User clicked reconnect from toast');
                connectToAI();
              }
            }
          });

          console.log('[AILiveChatV2] Disconnected from AI Assistant - Auto-reconnection in progress');
        },

        onContextUpdated: (data: ContextUpdated) => {
          console.log('[AILiveChatV2] Context updated:', data);
          // No toast - silent context update
        },

        onAudioTranscription: handleAudioTranscription,
        onAudioChunk: handleAudioChunk,
        onAudioComplete: handleAudioComplete,
      });

      console.log('[AI VOICE CHECK LOG] [AILiveChatV2] ✅ Event handlers registered successfully:', {
        onAudioTranscription: !!handleAudioTranscription,
        onAudioChunk: !!handleAudioChunk,
        onAudioComplete: !!handleAudioComplete
      });
    } catch (error) {
      console.error('[AILiveChatV2] Connection failed:', error);
      setIsConnected(false);
      setIsConnecting(false);
      setConnectionStatus('disconnected');
      toast.error('Failed to connect to AI Assistant');
    }
  }, [isConnected, isConnecting, course, currentModule, currentContent, handleAudioTranscription, handleAudioChunk, handleAudioComplete]);

  // Setup WebSocket connection handlers (but don't auto-connect)
  useEffect(() => {
    // DON'T auto-connect - connectToAI is available as a callback

    // Cleanup on unmount
    return () => {
      // DON'T disconnect - keep connection alive for persistence
      // Connection will be reused when component remounts
      console.log('[AILiveChatV2] Component unmounting, keeping connection alive');
    };
  }, []);

  // Initialize messages from persisted storage on mount
  useEffect(() => {
    const persistedMessages = learningAssistantV2Service.getPersistedMessages();
    if (persistedMessages.length > 0) {
      console.log('[AILiveChatV2] Restoring', persistedMessages.length, 'persisted messages');
      setMessages(persistedMessages);
    }
  }, []);

  // Persist messages to service whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      learningAssistantV2Service.setPersistedMessages(messages);
    }
  }, [messages]);

  // Subscribe to connection state changes from service
  useEffect(() => {
    const unsubscribe = learningAssistantV2Service.subscribeToConnectionState((connected) => {
      console.log('[AILiveChatV2] Connection state changed:', connected);
      setIsConnected(connected);
      setConnectionStatus(connected ? 'connected' : 'disconnected');

      // If connected and we have a session, get it
      if (connected) {
        const currentSessionId = learningAssistantV2Service.getCurrentSessionId();
        if (currentSessionId) {
          setSessionId(currentSessionId);
        }

        // CRITICAL: If we're connected but handlers aren't registered yet,
        // register them now (happens when component remounts with existing connection)
        if (!isConnecting) {
          console.log('[AILiveChatV2] Detected existing connection on mount, registering handlers');
          connectToAI();
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isConnecting, connectToAI]);

  // Update context when module/content changes
  useEffect(() => {
    // Skip if no module (initial render)
    if (!currentModule) return;

    // If connected, update context immediately
    if (isConnected && sessionId) {
      console.log('[AILiveChatV2] Context changed, updating:', {
        module: currentModule.title,
        content: currentContent?.title || 'None'
      });

      learningAssistantV2Service.updateContext({
        session_id: sessionId,
        course_id: courseId,
        module_id: currentModule._id || currentModule.id || '',
        scene_id: currentContent?._id || currentContent?.id || '',
        scene_number: currentContent?.scene_number,
      });
    }
  }, [isConnected, sessionId, currentModule, currentContent, courseId]);

  // Send message
  const sendMessage = useCallback(async (messageText?: string) => {
    const textToSend = messageText || inputMessage;
    if (!textToSend.trim() || !isConnected || isLoading) return;

    console.log('🎯 [UI] Sending message with language:', {
      message: textToSend,
      selectedLanguage,
      timestamp: new Date().toISOString()
    });

    // Add user message
    const userMessage = createUserMessage(textToSend);
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setFirstChunkTime(null);

    try {
      // Start streaming
      learningAssistantV2Service.startTextChat({
        course_id: courseId,
        module_id: currentModule?._id || currentModule?.id || '',
        scene_id: currentContent?._id || currentContent?.id || '',
        scene_number: currentContent?.scene_number,
        message: textToSend,
        language: selectedLanguage,
        session_id: sessionId || undefined,
      });

      console.log('✅ [UI] Message sent to service with language:', selectedLanguage);
    } catch (error) {
      console.error('[AILiveChatV2] Error starting chat:', error);
      toast.error('Failed to send message');
      setIsLoading(false);
    }
  }, [inputMessage, isConnected, isLoading, courseId, currentModule, currentContent, selectedLanguage, sessionId]);

  // Clear conversation
  // Stop audio playback
  const stopAudioPlayback = useCallback(() => {
    console.log('[AILiveChatV2] Stopping audio playback');

    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.currentTime = 0;
      audioPlayerRef.current.src = '';
    }

    // Clear audio chunks
    audioChunksRef.current = [];

    setIsPlayingAudio(false);
  }, []);

  const clearConversation = useCallback(() => {
    // Stop audio if playing
    if (isPlayingAudio) {
      stopAudioPlayback();
    }

    setMessages([]);
    setSessionId(null);
    streamingMessageIdRef.current = null;

    // Clear persisted messages
    learningAssistantV2Service.clearPersistedMessages();

    // toast.success('Conversation cleared');
    console.log('[AILiveChatV2] Conversation cleared');
  }, [isPlayingAudio, stopAudioPlayback]);

  // Helper: Force stop recording (for mode switch or disconnect)
  const forceStopRecording = useCallback(() => {
    console.log('[AILiveChatV2] Force stopping recording');

    // Clear server-based silence timer
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    // Clear local silence timer
    if (localSilenceTimerRef.current) {
      clearTimeout(localSilenceTimerRef.current);
      localSilenceTimerRef.current = null;
    }

    // Stop recording
    if (mediaRecorderRef.current && typeof (mediaRecorderRef.current as any).stop === 'function') {
      try {
        (mediaRecorderRef.current as any).stop();
      } catch (error) {
        console.error('[AILiveChatV2] Error force stopping recording:', error);
      }
      mediaRecorderRef.current = null;
    }

    // Reset states
    setIsRecording(false);
    setIsLoading(false);
    setTranscriptionText('');
  }, []);

  // Disconnect handler
  const handleDisconnect = useCallback(() => {
    console.log('[AILiveChatV2] Manual disconnect requested');

    // Force stop recording if active
    if (isRecording) {
      forceStopRecording();
    }

    // Stop audio playback
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.src = '';
    }

    // Clear audio chunks
    audioChunksRef.current = [];

    // Disconnect from service
    learningAssistantV2Service.disconnect();

    // State will be updated by onDisconnect handler
  }, [isRecording, forceStopRecording]);

  // Audio: Start recording (using Web Audio API for LINEAR16 PCM)
  const startRecording = useCallback(async () => {
    try {
      console.log('[AILiveChatV2] Starting audio recording...');

      // Check if connected
      if (!isConnected) {
        toast.error('Please connect to AI Assistant first');
        return;
      }

      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000, // Match backend expectation
        }
      });

      // Use AudioContext to get PCM data
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: 16000
      });
      const source = audioContext.createMediaStreamSource(stream);

      setTranscriptionText('');

      // Start audio chat on server FIRST
      learningAssistantV2Service.startAudioChat({
        session_id: sessionId || undefined,
        course_id: courseId,
        module_id: currentModule?._id || currentModule?.id,
        scene_id: currentContent?._id || currentContent?.id,
        scene_number: currentContent?.scene_number,
        language: selectedLanguage,
        return_audio: true,
      });

      // Create a flag to track if recording is active
      let isRecordingActive = true;
      let audioNode: AudioWorkletNode | ScriptProcessorNode | null = null;

      // Helper function to detect if there's audio activity (voice detection)
      const detectAudioActivity = (inputData: Float32Array): boolean => {
        // Calculate RMS (Root Mean Square) to detect audio level
        let sum = 0;
        for (let i = 0; i < inputData.length; i++) {
          sum += inputData[i] * inputData[i];
        }
        const rms = Math.sqrt(sum / inputData.length);

        // Threshold for voice detection (adjust if needed)
        // 0.01 = 1% of max volume, good for detecting speech
        const voiceThreshold = 0.01;

        return rms > voiceThreshold;
      };

      // Helper function to process audio data
      const processAudioData = (inputData: Float32Array) => {
        if (!isRecordingActive) {
          return;
        }

        // LOCAL SILENCE DETECTION: Detect audio activity
        const hasAudioActivity = detectAudioActivity(inputData);

        if (hasAudioActivity) {
          // Voice detected! Update activity timestamp
          const timeSinceLastActivity = Date.now() - lastAudioActivityRef.current;

          // Log voice detection (throttle to avoid spam - only log if > 500ms since last)
          if (timeSinceLastActivity > 500) {
            console.log('[AI SILENCE CHECK] 🔊 Audio activity detected - resetting timer');
          }

          lastAudioActivityRef.current = Date.now();

          // Reset local silence timer if it exists
          if (localSilenceTimerRef.current) {
            clearTimeout(localSilenceTimerRef.current);
            localSilenceTimerRef.current = null;
          }

          // Set new local silence timer (3 seconds as per requirement)
          localSilenceTimerRef.current = setTimeout(() => {
            const silenceDuration = Date.now() - lastAudioActivityRef.current;
            console.log('[AI SILENCE CHECK] 🔕 LOCAL SILENCE DETECTED - No audio for', silenceDuration / 1000, 'seconds');
            console.log('[AI SILENCE CHECK] ⏱️ Auto-stopping recording (local detection)...');

            // Stop recording
            if (mediaRecorderRef.current && typeof (mediaRecorderRef.current as any).stop === 'function') {
              try {
                (mediaRecorderRef.current as any).stop();
                console.log('[AI SILENCE CHECK] ✅ Recording stopped successfully (local silence detection)');
                // toast.success('Recording stopped (3s silence detected)', {
                //   duration: 2000,
                //   icon: '⏱️'
                // });
                console.log('[AILiveChatV2] Recording stopped (3s silence detected)');
              } catch (error) {
                console.error('[AI SILENCE CHECK] ❌ Error auto-stopping:', error);
              }
            }
          }, 3000); // 3 seconds as requested
        }

        // Use Web Worker for conversion if available, otherwise fallback to main thread
        if (audioWorkerRef.current) {
          // OPTIMIZATION: Offload CPU-intensive conversion to worker thread
          try {
            const chunkId = audioChunkIdRef.current++;
            audioWorkerRef.current.postMessage({
              inputData: Array.from(inputData),
              chunkId: chunkId
            });
          } catch (error) {
            console.error('[AILiveChatV2] Error posting to audio worker:', error);
            processAudioOnMainThread(inputData);
          }
        } else {
          processAudioOnMainThread(inputData);
        }
      };

      // Try to use modern AudioWorkletNode first, fallback to ScriptProcessorNode
      try {
        // Load the AudioWorklet processor
        await audioContext.audioWorklet.addModule('/audio-capture-processor.js');

        // Create AudioWorkletNode (modern, no deprecation warning)
        const workletNode = new AudioWorkletNode(audioContext, 'audio-capture-processor');

        // Listen for audio data from the worklet
        workletNode.port.onmessage = (event) => {
          if (!isRecordingActive) {
            return;
          }
          const audioData = new Float32Array(event.data.audioData);
          processAudioData(audioData);
        };

        source.connect(workletNode);
        audioNode = workletNode;

        console.log('[AILiveChatV2] ✅ Using modern AudioWorkletNode for audio capture');
      } catch (error) {
        // Fallback to ScriptProcessorNode for older browsers
        console.warn('[AILiveChatV2] AudioWorklet not supported, falling back to ScriptProcessorNode:', error);

        const processor = audioContext.createScriptProcessor(4096, 1, 1);

        processor.onaudioprocess = (e) => {
          if (!isRecordingActive) {
            return;
          }
          const inputData = e.inputBuffer.getChannelData(0);
          processAudioData(inputData);
        };

        source.connect(processor);
        // Connect to destination to keep processor alive (required for ScriptProcessorNode)
        processor.connect(audioContext.destination);
        audioNode = processor;

        console.log('[AILiveChatV2] ⚠️ Using deprecated ScriptProcessorNode (fallback)');
      }

      // Helper function for main thread processing (fallback)
      function processAudioOnMainThread(inputData: Float32Array) {
        // Convert float32 to int16 (LINEAR16 format)
        const int16Array = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }

        // Convert to base64
        const uint8Array = new Uint8Array(int16Array.buffer);
        const base64 = btoa(String.fromCharCode.apply(null, Array.from(uint8Array)));

        // Send chunk to server
        try {
          learningAssistantV2Service.sendAudioChunk(base64);
        } catch (error) {
          console.error('[AILiveChatV2] Error sending audio chunk:', error);
          isRecordingActive = false;
        }
      }

      // Store references for cleanup
      (mediaRecorderRef as any).current = {
        stream,
        audioContext,
        audioNode,
        source,
        stop: () => {
          console.log('[AILiveChatV2] Stopping audio recording - cleaning up');

          // Set flag to stop processing
          isRecordingActive = false;

          // Disconnect audio nodes FIRST
          try {
            if (audioNode) {
              // Handle both AudioWorkletNode and ScriptProcessorNode
              if ('onaudioprocess' in audioNode) {
                // ScriptProcessorNode
                (audioNode as ScriptProcessorNode).onaudioprocess = null;
              } else if ('port' in audioNode) {
                // AudioWorkletNode
                (audioNode as AudioWorkletNode).port.onmessage = null;
              }
              audioNode.disconnect();
            }
            source.disconnect();
          } catch (error) {
            console.error('[AILiveChatV2] Error disconnecting audio nodes:', error);
          }

          // Stop all media tracks
          try {
            stream.getTracks().forEach(track => {
              console.log('[AILiveChatV2] Stopping track:', track.kind);
              track.stop();
            });
          } catch (error) {
            console.error('[AILiveChatV2] Error stopping tracks:', error);
          }

          // Close audio context
          try {
            audioContext.close();
          } catch (error) {
            console.error('[AILiveChatV2] Error closing audio context:', error);
          }

          // Send end signal to server
          try {
            learningAssistantV2Service.endAudioStream();
          } catch (error) {
            console.error('[AILiveChatV2] Error ending audio stream:', error);
          }

          // Update state
          setIsRecording(false);

          console.log('[AILiveChatV2] Audio recording stopped successfully');
        }
      };

      setIsRecording(true);
      setIsLoading(true);

      // Initialize timer tracking
      lastTranscriptionTimeRef.current = null;
      silenceTimerRef.current = null;

      // toast.success('Recording started - speak now');
      console.log('[AILiveChatV2] Recording started - speak now');
      console.log('[AI VOICE CHECK LOG] [AILiveChatV2] ✅ Recording started successfully');
      console.log('[AI VOICE CHECK LOG] [AILiveChatV2] 🎯 Auto-stop enabled: Will stop after 4 seconds of silence');
    } catch (error) {
      console.error('Failed to start recording:', error);

      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          toast.error('Microphone access denied. Please allow microphone access.');
        } else if (error.name === 'NotFoundError') {
          toast.error('No microphone found. Please connect a microphone.');
        } else if (error.message.includes('not connected')) {
          toast.error('WebSocket not connected. Please reconnect.');
        } else {
          toast.error(`Failed to start recording: ${error.message}`);
        }
      } else {
        toast.error('Microphone access denied');
      }

      setIsRecording(false);
      setIsLoading(false);
    }
  }, [isConnected, sessionId, courseId, currentModule, currentContent, selectedLanguage]);

  // Audio: Stop recording
  const stopRecording = useCallback(() => {
    console.log('[AILiveChatV2] Stopping recording manually');

    // Clear server-based silence timer
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    // Clear local silence timer
    if (localSilenceTimerRef.current) {
      clearTimeout(localSilenceTimerRef.current);
      localSilenceTimerRef.current = null;
    }

    if (mediaRecorderRef.current && typeof (mediaRecorderRef.current as any).stop === 'function') {
      (mediaRecorderRef.current as any).stop();
      mediaRecorderRef.current = null; // Clear reference
      // toast.success('Recording stopped, processing...');
      console.log('[AILiveChatV2] Recording stopped, processing...');
    }
  }, []);

  // Initialize audio player
  useEffect(() => {
    const audio = new Audio();
    audio.onended = () => setIsPlayingAudio(false);
    audioPlayerRef.current = audio;

    return () => {
      audio.pause();
      audio.src = '';
    };
  }, []);

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-gray-50 to-gray-100 relative">
      {/* Loading Overlay - Shows during connection with minimum display time */}
      {showLoader && (
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/95 via-purple-900/95 to-pink-900/95 flex items-center justify-center z-50 backdrop-blur-md">
          <div className="text-center">
            {/* Animated Bot Icon */}
            <div className="relative mb-6 inline-block">
              <div className="absolute inset-0 bg-white/20 rounded-full animate-ping"></div>
              <div className="relative w-20 h-20 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm border-4 border-white/30">
                <Bot className="w-10 h-10 text-white animate-bounce" />
              </div>
            </div>

            {/* Loading Spinner */}
            <div className="flex items-center justify-center mb-4">
              <Loader2 className="w-12 h-12 text-white animate-spin" />
            </div>

            {/* Loading Text */}
            <h3 className="text-2xl font-bold text-white mb-2">Connecting to SkillBot</h3>
            <p className="text-white/80 text-sm mb-4">Establishing secure connection...</p>

            {/* Loading Dots */}
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Initial Connection Screen */}
      {connectionStatus === 'disconnected' && (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center z-10 backdrop-blur-sm p-4">
          <div className="max-w-md w-full">
            {/* Main Card */}
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
              {/* Header Section with Gradient */}
              <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6 text-white">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl -mr-20 -mt-20"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -ml-16 -mb-16"></div>

                {/* Logo/Icon */}
                <div className="relative flex items-center justify-center mb-3">
                  <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-xl border border-white/30">
                    <Bot className="w-8 h-8 text-white drop-shadow-lg" />
                  </div>
                </div>

                {/* Title */}
                <div className="relative text-center">
                  <h2 className="text-2xl font-bold mb-1 tracking-tight">
                    SkillBot AI
                  </h2>
                  <p className="text-white/90 text-xs font-medium">
                    Your Intelligent Learning Companion
                  </p>
                </div>
              </div>

              {/* Content Section */}
              <div className="p-5">
                {/* Features Grid */}
                <div className="grid grid-cols-2 gap-2.5 mb-5">
                  <div className="group bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-lg p-3 border border-indigo-200/50 hover:shadow-md transition-all duration-200">
                    <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Zap className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-[11px] font-bold text-gray-900 mb-0.5">Ultra-Fast</h3>
                    <p className="text-[9px] text-gray-600 leading-snug">Instant responses &lt;200ms</p>
                  </div>

                  <div className="group bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-lg p-3 border border-purple-200/50 hover:shadow-md transition-all duration-200">
                    <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <MessageSquare className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-[11px] font-bold text-gray-900 mb-0.5">Real-Time Chat</h3>
                    <p className="text-[9px] text-gray-600 leading-snug">Live streaming text</p>
                  </div>

                  <div className="group bg-gradient-to-br from-pink-50 to-pink-100/50 rounded-lg p-3 border border-pink-200/50 hover:shadow-md transition-all duration-200">
                    <div className="w-8 h-8 rounded-lg bg-pink-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Mic className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-[11px] font-bold text-gray-900 mb-0.5">Voice Mode</h3>
                    <p className="text-[9px] text-gray-600 leading-snug">Natural conversations</p>
                  </div>

                  <div className="group bg-gradient-to-br from-green-50 to-green-100/50 rounded-lg p-3 border border-green-200/50 hover:shadow-md transition-all duration-200">
                    <div className="w-8 h-8 rounded-lg bg-green-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Globe className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-[11px] font-bold text-gray-900 mb-0.5">Multilingual</h3>
                    <p className="text-[9px] text-gray-600 leading-snug">21 languages</p>
                  </div>
                </div>

                {/* Connect Button */}
                <button
                  onClick={connectToAI}
                  disabled={connectionStatus === 'connecting'}
                  className={`group relative w-full py-3.5 px-5 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white font-bold rounded-xl transition-all duration-300 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] overflow-hidden ${
                    connectionStatus === 'connecting' ? 'opacity-90 animate-pulse' : ''
                  }`}
                >
                  {/* Animated Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>

                  {/* Loading Spinner Ring */}
                  {connectionStatus === 'connecting' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-full h-full border-2 border-white/30 border-t-white rounded-xl animate-spin"></div>
                    </div>
                  )}

                  {connectionStatus === 'connecting' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin relative z-10" />
                      <span className="text-sm relative z-10">Connecting to SkillBot...</span>
                    </>
                  ) : (
                    <>
                      <Activity className="w-4 h-4 relative z-10 group-hover:rotate-180 transition-transform duration-500" />
                      <span className="text-sm relative z-10">Start Learning Session</span>
                    </>
                  )}
                </button>

                {/* Tech Stack Badges */}
                {/* <div className="mt-4 flex items-center justify-center gap-1.5 flex-wrap">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-[9px] font-semibold border border-blue-200">
                    <Activity className="w-2.5 h-2.5" />
                    WebSocket
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full text-[9px] font-semibold border border-purple-200">
                    <Sparkles className="w-2.5 h-2.5" />
                    Gemini 2.0
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 rounded-full text-[9px] font-semibold border border-green-200">
                    <Globe className="w-2.5 h-2.5" />
                    Google Cloud
                  </span>
                </div> */}

                {/* Bottom Text */}
                <p className="text-center text-[10px] text-gray-500 mt-3 font-medium">
                  Secure • Private • Context-Aware Learning
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-3 text-white shadow-md">
        {/* Row 1: Title and Status */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <Bot className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold leading-tight">
                SkillBot
              </h3>
              <p className="text-[11px] text-white/80 font-medium">AI Learning Personal Assistant</p>
            </div>
          </div>

          {/* Status Indicator */}
          <div>
            {connectionStatus === 'connected' && (
              <div className="flex items-center gap-2 bg-green-500/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-green-400/30">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium">Connected</span>
              </div>
            )}
            {connectionStatus === 'connecting' && (
              <div className="flex items-center gap-2 bg-yellow-500/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-yellow-400/30">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="text-xs font-medium">Connecting...</span>
              </div>
            )}
            {connectionStatus === 'disconnected' && (
              <div className="flex items-center gap-2 bg-gray-500/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-gray-400/30">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <span className="text-xs font-medium">Offline</span>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Mode Toggle, Language Selector and Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Mode Toggle - Compact */}
          <div className="flex items-center gap-0.5 bg-white/10 backdrop-blur-sm rounded-lg p-0.5 flex-shrink-0">
            <button
              onClick={() => {
                if (audioMode && isRecording) {
                  console.log('[AILiveChatV2] Switching to text mode, stopping recording');
                  forceStopRecording();
                }
                setAudioMode(false);
              }}
              className={`p-2 rounded-md transition-all duration-200 ${
                !audioMode
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
              title="Text Chat"
            >
              <MessageSquare className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setAudioMode(true);
              }}
              className={`p-2 rounded-md transition-all duration-200 ${
                audioMode
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
              title="Voice Chat"
            >
              <Mic className="w-4 h-4" />
            </button>
          </div>

          {/* Language Selector */}
          <div className="flex-1">
            <Select<LanguageOption>
              value={languageSelectOptions.find((opt) => opt.value === selectedLanguage)}
              onChange={(option) => {
                if (option) {
                  setSelectedLanguage(option.value);
                  // toast.success(`Language: ${option.label}`);
                  console.log('[AILiveChatV2] Language changed to:', option.label);
                }
              }}
              options={languageSelectOptions}
              components={{
                Option: CustomOption,
                SingleValue: CustomSingleValue,
              }}
              styles={customSelectStyles}
              isSearchable={true}
              placeholder="Select language..."
              menuPlacement="auto"
              classNamePrefix="language-select"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Reset Button */}
            <button
              onClick={clearConversation}
              className="p-2.5 hover:bg-white/20 rounded-lg transition-all bg-white/10 backdrop-blur-sm shadow-sm hover:shadow-md"
              title="Reset conversation"
            >
              <RefreshCw className="w-4 h-4" />
            </button>

            {/* Connection Toggle */}
            {connectionStatus === 'connected' ? (
              <button
                onClick={handleDisconnect}
                className="p-2.5 hover:bg-white/20 rounded-lg transition-all bg-white/10 backdrop-blur-sm shadow-sm hover:shadow-md"
                title="Disconnect"
              >
                <Activity className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={connectToAI}
                disabled={connectionStatus === 'connecting'}
                className="p-2.5 hover:bg-white/20 rounded-lg transition-all bg-white/10 backdrop-blur-sm shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                title="Connect"
              >
                <Activity className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-1">
        {messages.map((message) => (
          <MessageComponent
            key={message.id}
            message={message}
            onSendSuggestion={sendMessage}
            isLoading={isLoading}
          />
        ))}

        {/* Loading indicator with event status */}
        {isLoading && currentEventStatus && (
          <div className="flex justify-start mb-2 animate-fadeIn">
            <div className="bg-white text-gray-800 shadow-sm border border-gray-100 rounded-xl p-2.5">
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <Bot className="w-3 h-3 text-white" />
                </div>
                <div className="flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" />
                  <span className="text-xs font-medium text-gray-700">{currentEventStatus}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - CLEAN UI */}
      <div className="border-t border-gray-200 bg-gradient-to-br from-white to-gray-50 p-3 sm:p-4 shadow-inner">
        {/* Audio playback indicator with stop button */}
        {isPlayingAudio && (
          <div className="flex items-center justify-center gap-2 text-xs text-indigo-600 animate-fadeIn bg-indigo-50 px-3 py-2 rounded-xl border border-indigo-200 mb-3">
            <div className="flex items-center gap-2">
              <Volume2 className="w-4 h-4 animate-pulse" />
              <span className="font-semibold">Playing...</span>
            </div>
            <button
              onClick={stopAudioPlayback}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg font-medium"
              title="Stop audio playback"
            >
              <Square className="w-3.5 h-3.5" />
              <span>Stop</span>
            </button>
          </div>
        )}

        {/* Text Mode Input */}
        {!audioMode && (
          <div className="flex gap-1.5">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder={isConnected ? "Type your message..." : "Connecting to AI Assistant..."}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-xs placeholder:text-gray-400 bg-white"
              disabled={!isConnected || isLoading}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!isConnected || isLoading || !inputMessage.trim()}
              className="px-3 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Voice Mode Input - MODERNIZED */}
        {audioMode && (
          <div className="space-y-3">
            {/* Transcription display - COMMENTED OUT FOR CLEANER UI */}
            {/* {transcriptionText && (
              <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 border-2 border-indigo-300 rounded-2xl p-3 animate-fadeIn shadow-md">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-md">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-gray-900 leading-relaxed font-medium">
                      {transcriptionText.replace('|INTERIM|', '')}
                      <span className="inline-block w-0.5 h-4 ml-1 bg-indigo-600 animate-pulse" />
                    </div>
                    {transcriptionText.includes('|INTERIM|') && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-indigo-600">
                        <div className="flex gap-0.5">
                          <span className="w-1 h-1 bg-indigo-600 rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                          <span className="w-1 h-1 bg-indigo-600 rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                          <span className="w-1 h-1 bg-indigo-600 rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                        </div>
                        <span className="font-semibold italic">Transcribing live...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )} */}

            {/* Record button - MODERN DESIGN with BUG FIX 2 */}
            <div className="flex items-center justify-center py-2">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={!isConnected || (isLoading && !isRecording)}
                className={`group relative px-8 py-4 rounded-2xl transition-all duration-300 flex items-center gap-3 font-bold text-sm shadow-2xl transform hover:scale-105 active:scale-95 ${
                  isRecording
                    ? 'bg-gradient-to-br from-red-500 via-red-600 to-pink-600 hover:from-red-600 hover:via-red-700 hover:to-pink-700 text-white'
                    : 'bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white'
                } disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none`}
              >
                {isRecording ? (
                  <>
                    <div className="absolute -inset-1 bg-red-400 rounded-2xl animate-ping opacity-20" />
                    <Square className="w-5 h-5 relative z-10" />
                    <span className="relative z-10">Stop Recording</span>
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5" />
                    <span>Start Recording</span>
                  </>
                )}
              </button>
            </div>

            {/* Recording status - MODERN DESIGN */}
            {isRecording && (
              <div className="flex items-center justify-center gap-2 animate-fadeIn bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl py-2 px-4">
                <div className="relative flex items-center justify-center">
                  <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="absolute w-3 h-3 bg-red-500 rounded-full animate-ping" />
                </div>
                <span className="text-xs font-bold text-red-700">LISTENING</span>
                <span className="text-xs text-gray-600">• Speak clearly into your microphone</span>
              </div>
            )}

            {/* Helper text removed for cleaner UI */}
          </div>
        )}
      </div>
    </div>
  );
}
